import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchuserLevels } from "../../RTK/userlevels/GetAll";
import {
  TrendingUp,
  DollarSign,
  Award,
  Pencil,
  Trash2,
  Plus,
} from "lucide-react";
import AddLevel from "./popups/AddLevel";
import ConfirmDelete from "./popups/ConfirmDelete";
import { deletelevel } from "../../RTK/userlevels/DeleteLevel";
import { toast } from "react-toastify";
import EditLevel from "./popups/EditLevel";
import Loader from "../../components/loader/Loader";

const UserLevels = () => {
  const dispatch = useDispatch();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteLevelId, setDeleteLevelId] = useState(null); // Store the level ID to delete

  const [isEditOpen, setIsEditOpen] = useState(false); // New state for edit modal
  const [editLevelData, setEditLevelData] = useState(null); // Store data for the level to edit

  const { userLevels, loading, error } = useSelector(
    (state) => state.userLevels
  );

  useEffect(() => {
    dispatch(fetchuserLevels());
  }, [dispatch]);

  const handleDelete = async () => {
    try {
      await dispatch(deletelevel(deleteLevelId)).unwrap();
      await dispatch(fetchuserLevels()).unwrap(); // Refresh the list
      toast.success("Level deleted successfully!");
    } catch (error) {
      toast.error(error || "Failed to delete Level");
    } finally {
      setShowConfirmDelete(false); // Close the confirmation modal
      setDeleteLevelId(null); // Reset the delete ID
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteLevelId(id); // Set the ID of the level to delete
    setShowConfirmDelete(true); // Show the confirmation modal
  };

  const handleEditClick = (level) => {
    setEditLevelData(level); // Set the data of the level to edit
    setIsEditOpen(true); // Open the edit modal
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 min-h-screen p-6">
      <div className="">

        <div className="flex justify-between items-center mb-8">
          <h1 className="sm:text-2xl lg:text-2xl font-bold  bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-all  transform ">مستويات المستخدم</h1>
          <button
            onClick={() => setIsFormOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-lg flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة مستوى</span>
          </button>
        </div>

        <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {userLevels.map((level) => (
            <div
              key={level.id}
              className="group hover:scale-105 transition-all duration-300 overflow-hidden rounded-2xl relative bg-gradient-to-br from-gray-800 to-gray-900 shadow-xl border border-gray-700"
            >
              <div
                className="absolute inset-0 bg-gradient-to-r opacity-20"
                style={{
                  background: `linear-gradient(135deg, ${level.hex_color}66, ${level.hex_color}99)`,
                }}
              />

              <div className="absolute top-2 left-1/2 -translate-x-1/2 flex gap-40 opacity-0 group-hover:opacity-100 transition-all duration-300 transform -translate-y-2 group-hover:translate-y-0">
                <button
                  onClick={() => handleEditClick(level)} // Pass the level data
                  className="p-2.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-500/50 cursor-pointer group/edit"
                  aria-label="Edit level"
                >
                  <Pencil className="w-4 h-4 text-white group-hover/edit:scale-110 transition-transform" />
                </button>
                <button
                  onClick={() => handleDeleteClick(level.id)} // Pass the level ID here
                  className="p-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-red-500/50 cursor-pointer group/delete"
                  aria-label="Delete level"
                >
                  <Trash2 className="w-4 h-4 text-white group-hover/delete:scale-110 transition-transform" />
                </button>
              </div>

              <div className="p-6">
                <div className="relative">
                  <div className="flex justify-center mb-6">
                    <div
                      className="w-24 h-24 rounded-full overflow-hidden border-4 p-1 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm shadow-2xl"
                      style={{ borderColor: level.hex_color }}
                    >
                      <img
                        src={level.icon}
                        alt={level.name}
                        className="w-full h-full object-cover rounded-full transition-transform duration-300 group-hover:scale-110"
                      />
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-center mb-6 text-white">
                    {level.name}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 transition-colors">
                    <Award
                      className="w-5 h-5"
                      style={{ color: level.hex_color }}
                    />
                    <span className="text-white/90">المستوى {level.level}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 transition-colors">
                    <DollarSign
                      className="w-5 h-5"
                      style={{ color: level.hex_color }}
                    />
                    <span className="text-white/90">
                      الحد الأدنى ${" "}
                      {Number(level.min_monthly_spending).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl backdrop-blur-sm bg-white/5 hover:bg-white/10 transition-colors">
                    <TrendingUp
                      className="w-5 h-5"
                      style={{ color: level.hex_color }}
                    />
                    <span className="text-white/90">
                      نسبة الربح {level.profit_percentage}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <Loader/>
        )}
      </div>
      <AddLevel isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />

      {showConfirmDelete && (
        <ConfirmDelete
          onConfirm={handleDelete}
          onCancel={() => setShowConfirmDelete(false)}
        />
      )}

      <EditLevel
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        levelData={editLevelData}
      />
    </div>
  );
};

export default UserLevels;
